/**
 * 기준 정보 설정 — 학생 기록의 값을 미리 정해 두는 화면(오너 2026-10-10 「상태, 반, 학교/학년, 부서 등을 미리 설정하는 화면」).
 * 반 관리 화면 머리의 탭으로 연다. 매니저 전용(요청서 §10 「부서·학교·코드·설정 — 교사 읽기만」).
 *
 *   부서      B1 · B2 · B3 — 이름 · 부서 자동 선택 기준 · 순서 · 감춤 · 지우기
 *   학교      B4 · B5 · B6 — 찾기 · 추가(같은 이름·학교급·지역이면 중복) · 수정 · 지우기
 *   사유·분류  A1 · A2 · A3 — 퇴원 사유 · 휴원 사유 · 퇴반 사유 · 메모 분류 · 출결 상태. 감춘 값은 고르기에서 빠지고 지난 기록에는 남음(V21)
 *
 * 지우기(백엔드 1단계 전달 §2 · §4-B) — 딸린 기록이 하나도 없을 때만 서버가 지운다. 서버는 지난 기록 · 지운 표시 기록 · 비활성 반까지
 * 세므로 화면이 「지울 수 있다」를 미리 판단하지 않는다(지금 학생 수 member_count 는 오늘 기준이라 근거가 못 된다). 단추는 늘 두고,
 * 확인 창을 거친 뒤 거절(409 in_use · last_of_kind)은 그 줄 안에 보인다. 서버 문구가 몇 명 · 몇 건인지 말한다.
 * 학교 합치기는 없다(오너 결정 2026-10-12) — 잘못 넣은 학생은 학생 상세 학교 기간 [정정]으로 옮긴 뒤 그 학교를 지운다(같은 문서 §3).
 *
 * 학년 올림 탭은 없다 — 학년은 학교·학년 기간 행의 값 그대로이고 자동으로 오르지 않는다(오너 결정 2026-10-12,
 * 백엔드 0단계 전달 §3-1). 올림일 설정도 서버에 없다.
 * 학생 상태(수강 중 · 휴원 · 퇴원 예정 · 등록 예정 · 퇴원)는 기간 기록에서 계산하는 규칙이라 설정하지 않는다 — 설정하는 것은 그 사유다.
 */
import React, { useState } from 'react';
import { AcademyButton, ActionButton, actionIcon, compactInputBase } from './controls';
import { useAuth } from '../../context/AuthContext';
import { Modal } from '../common/Modal';
import {
  createCode, createSchool, deleteCode, deleteDepartment, deleteSchool, listCodesFull, listDepartments, listSchools, updateCode,
  updateDepartment, updateSchool,
} from './academyApi';
import { Box, ErrorNotice, SegTabs, Spinner, ToneBadge, notifyAcademyChanged, useAcademyVersion, useAsync } from './ui';
import { ErrorBox, Field, FormModal, Note, TextInput } from './modals/form';
import DepartmentLevelsField, { departmentLevelsLabel } from './modals/DepartmentLevelsField';
import { DepartmentAddModal } from './modals/ClassModals';

const LEVELS = [['ELEM', '초등'], ['MIDDLE', '중등'], ['HIGH', '고등']];
const levelName = (v) => (LEVELS.find(([k]) => k === v) || [null, '—'])[1];

/**
 * 줄 단위 저장 — 실패하면 **그 줄 안에** 사유. key 는 그 줄(값 · 부서 · 학교 번호)이다.
 * 지우기 · 감추기 거절은 「학생 3명이 있어 …」처럼 그 줄의 사정을 말하므로 표 아래가 아니라 그 줄에서 읽혀야 한다(백엔드 1단계 전달 §4-B).
 * 줄이 아닌 일(새 값 추가)은 key 'add' 로 표 아래에 보인다.
 */
function useRowSave() {
  const [failed, setFailed] = useState(null); // { key, error }
  const run = async (key, fn) => {
    setFailed(null);
    try { await fn(); notifyAcademyChanged(); } catch (e) { setFailed({ key, error: e }); }
  };
  const errorOf = (key) => (failed && failed.key === key ? failed.error : null);
  /** 보던 묶음이 바뀌면 지난 거절을 비운다 — 다른 묶음의 「추가」 아래에 옛 거절이 남지 않게(백엔드 4단계 전달 §4-G) */
  const clear = () => setFailed(null);
  return { run, errorOf, clear };
}

/** 표의 줄 바로 아래에 그 줄의 거절 — 칸을 가로질러 한 줄로 */
function RowError({ error, colSpan }) {
  if (!error) return null;
  return <tr><td colSpan={colSpan} className="pb-2"><ErrorBox error={error} /></td></tr>;
}

/**
 * 지우기 확인 — 창은 묻기만 한다. 지우기는 창을 닫은 뒤 그 줄에서 하고, 거절은 그 줄 안에 보인다.
 * 지울 수 있는지는 묻지 않는다(서버만 안다) — 안 되면 서버가 까닭을 말한다.
 */
function ConfirmDelete({ title, children, onConfirm, onClose }) {
  const footer = (
    <div className="flex w-full items-center justify-end gap-2">
      <AcademyButton type="button" onClick={onClose} variant="secondary" size="sm">취소</AcademyButton>
      <AcademyButton type="button" onClick={() => { onClose(); onConfirm(); }} variant="danger" size="sm">지우기</AcademyButton>
    </div>
  );
  return (
    <Modal isOpen onClose={onClose} title={title} size="sm" footer={footer}>
      <Note tone="warn">{children}</Note>
    </Modal>
  );
}

// ── 부서 ──

function DepartmentEditModal({ dept, onClose }) {
  const { getAccessToken } = useAuth();
  const [name, setName] = useState(dept.name);
  const [levels, setLevels] = useState(dept.school_levels);
  const [sort, setSort] = useState(String(dept.sort_order ?? 0));
  return (
    <FormModal onClose={onClose} title={`${dept.name} — 부서 수정`} canSubmit={!!name.trim()}
      onSubmit={() => updateDepartment(dept.department_id, { name: name.trim(), schoolLevels: levels, sortOrder: Number(sort) }, getAccessToken)}>
      <Field label="부서 이름" required><TextInput value={name} onChange={setName} /></Field>
      <DepartmentLevelsField value={levels} onChange={setLevels} />
      <Field label="순서" hint="작은 숫자부터 표시"><TextInput value={sort} onChange={setSort} inputMode="numeric" /></Field>
    </FormModal>
  );
}

function DepartmentsSettings() {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const r = useAsync((signal) => listDepartments({ includeArchived: true, signal }, getAccessToken), `set-depts-${version}`);
  const [modal, setModal] = useState(null);
  const row = useRowSave();
  const remove = (d) => row.run(d.department_id, () => deleteDepartment(d.department_id, getAccessToken));
  return (
    <Box title="부서" right={<AcademyButton type="button" onClick={() => setModal({ type: 'add' })}>부서 추가</AcademyButton>}>
      {r.loading && !r.data && <Spinner label="부서 불러오는 중" />}
      {r.error && <ErrorNotice error={r.error} onRetry={r.reload} compact />}
      {r.data && (
        <div className="overflow-x-auto"><table className="w-full min-w-[440px] text-xs">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 font-medium">순서</th>
              <th className="py-1 font-medium">부서</th>
              <th className="py-1 font-medium">부서 자동 선택 기준</th>
              <th className="py-1 text-right font-medium">지금 학생</th>
              <th className="py-1" aria-label="작업" />
            </tr>
          </thead>
          <tbody>
            {r.data.map((d) => (
              <React.Fragment key={d.department_id}>
                <tr className={`border-t border-gray-100 ${d.archived ? 'text-gray-400' : ''}`}>
                  <td className="py-1.5">{d.sort_order}</td>
                  <td className="py-1.5 font-medium">{d.name}{d.archived && <span className="ml-1"><ToneBadge tone="muted">감춤</ToneBadge></span>}</td>
                  <td className="py-1.5">{departmentLevelsLabel(d.school_levels)}</td>
                  <td className="py-1.5 text-right">{d.member_count ?? '—'}</td>
                  <td className="py-1.5 text-right">
                    <span className="flex justify-end gap-2">
                      <ActionButton iconOnly type="button" onClick={() => setModal({ type: 'edit', dept: d })}>수정</ActionButton>
                      <ActionButton iconOnly type="button" onClick={() => row.run(d.department_id, () => updateDepartment(d.department_id, { archived: !d.archived }, getAccessToken))}>
                        {d.archived ? '되살리기' : '감추기'}
                      </ActionButton>
                      <ActionButton iconOnly type="button" onClick={() => setModal({ type: 'delete', dept: d })} danger>지우기</ActionButton>
                    </span>
                  </td>
                </tr>
                <RowError error={row.errorOf(d.department_id)} colSpan={5} />
              </React.Fragment>
            ))}
          </tbody>
        </table></div>
      )}
      {modal?.type === 'add' && <DepartmentAddModal onClose={() => setModal(null)} />}
      {modal?.type === 'edit' && <DepartmentEditModal dept={modal.dept} onClose={() => setModal(null)} />}
      {modal?.type === 'delete' && (
        <ConfirmDelete title={`${modal.dept.name} — 부서 지우기`} onClose={() => setModal(null)} onConfirm={() => remove(modal.dept)}>
          {modal.dept.name}을(를) 삭제합니다.
        </ConfirmDelete>
      )}
    </Box>
  );
}

// ── 학교 ──

function SchoolFormModal({ school, onClose }) {
  const { getAccessToken } = useAuth();
  const [name, setName] = useState(school?.name || '');
  const [level, setLevel] = useState(school?.school_level || '');
  const [region, setRegion] = useState(school?.region || '');
  return (
    <FormModal onClose={onClose} title={school ? `${school.name} — 학교 수정` : '학교 추가'} canSubmit={!!name.trim() && !!level}
      top={(
        <fieldset className="space-y-2">
          <legend className="mb-1 text-xs font-medium text-gray-700">학교급<span className="ml-0.5 text-danger">*</span></legend>
          <div className="flex"><SegTabs tabs={LEVELS} value={level} onChange={setLevel} ariaLabel="학교급" /></div>
        </fieldset>
      )}
      onSubmit={() => (school
        ? updateSchool(school.school_id, { name: name.trim(), schoolLevel: level, region }, getAccessToken)
        : createSchool({ name: name.trim(), schoolLevel: level, region }, getAccessToken))}>
      <Field label="학교 이름" required><TextInput value={name} onChange={setName} placeholder="예: 한빛중학교" /></Field>
      <Field label="지역"><TextInput value={region} onChange={setRegion} /></Field>
    </FormModal>
  );
}

function SchoolsSettings() {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const [level, setLevel] = useState('');
  const [q, setQ] = useState('');
  const r = useAsync((signal) => listSchools({ level: level || undefined, signal }, getAccessToken), `set-schools-${level}-${version}`);
  const [modal, setModal] = useState(null);
  const row = useRowSave();
  const rows = (r.data || []).filter((x) => !q || x.name.includes(q));
  const remove = (x) => row.run(x.school_id, () => deleteSchool(x.school_id, getAccessToken));
  return (
    <Box title="학교" sub={`${rows.length}곳`} right={<AcademyButton type="button" onClick={() => setModal({ type: 'add' })}>학교 추가</AcademyButton>}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <SegTabs tabs={[['', '전체'], ...LEVELS]} value={level} onChange={setLevel} ariaLabel="학교급" />
        <input aria-label="학교 찾기" value={q} onChange={(e) => setQ(e.target.value)} placeholder="학교 이름" className={`ml-auto w-36 ${compactInputBase}`} />
      </div>
      {r.loading && !r.data && <Spinner label="학교 불러오는 중" />}
      {r.error && <ErrorNotice error={r.error} onRetry={r.reload} compact />}
      {r.data && (
        <div className="overflow-x-auto"><table className="w-full min-w-[440px] text-xs">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 font-medium">학교</th>
              <th className="py-1 font-medium">학교급</th>
              <th className="py-1 font-medium">지역</th>
              <th className="py-1 text-right font-medium">지금 학생</th>
              <th className="py-1" aria-label="작업" />
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => (
              <React.Fragment key={x.school_id}>
                <tr className="border-t border-gray-100">
                  <td className="py-1.5 font-medium text-gray-900">{x.name}</td>
                  <td className="py-1.5">{levelName(x.school_level)}</td>
                  <td className="py-1.5 text-gray-500">{x.region || '—'}</td>
                  <td className="py-1.5 text-right">{x.member_count ?? '—'}</td>
                  <td className="py-1.5 text-right">
                    <span className="flex justify-end gap-2">
                      <ActionButton iconOnly type="button" onClick={() => setModal({ type: 'edit', school: x })}>수정</ActionButton>
                      <ActionButton iconOnly type="button" onClick={() => setModal({ type: 'delete', school: x })} danger>지우기</ActionButton>
                    </span>
                  </td>
                </tr>
                <RowError error={row.errorOf(x.school_id)} colSpan={5} />
              </React.Fragment>
            ))}
          </tbody>
        </table></div>
      )}
      {modal?.type === 'add' && <SchoolFormModal onClose={() => setModal(null)} />}
      {modal?.type === 'edit' && <SchoolFormModal school={modal.school} onClose={() => setModal(null)} />}
      {modal?.type === 'delete' && (
        <ConfirmDelete title={`${modal.school.name} — 학교 지우기`} onClose={() => setModal(null)} onConfirm={() => remove(modal.school)}>
          {modal.school.name}을(를) 삭제합니다.
        </ConfirmDelete>
      )}
    </Box>
  );
}

// ── 사유 · 분류(코드) ──

const CODE_GROUPS = [
  ['withdraw_reason', '퇴원 사유', '퇴원 창에서 고름 — 퇴원 사유별 통계에 쓰임'],
  ['pause_reason', '휴원 사유', '휴원 창에서 고름'],
  ['class_leave_reason', '퇴반 사유', '반 이동 · 퇴원이 아닌 퇴반에서 고름'],
  ['note_category', '메모 분류', '메모 쓰기에서 고름'],
  ['attendance_status', '출결 상태', '출결 입력의 출석 · 지각 · 조퇴 · 결석 — 운영 값도 넷 중 하나의 종류를 가짐(결석 종류만 시간 불인정)'],
];
/** 출결 상태의 시스템 종류 — 새 출결 값은 넷 중 하나를 골라야 한다(400 system_kind_required) */
const ATTENDANCE_KINDS = [['PRESENT', '출석'], ['LATE', '지각'], ['EARLY_LEAVE', '조퇴'], ['ABSENT', '결석']];
/**
 * 시스템 종류 배지 — 출결 넷에 퇴원 「사유 모름」(UNKNOWN, 이관한 퇴원 학생용 · 백엔드 1단계 전달 §4-C)을 더한다.
 * 고르기 칸(ATTENDANCE_KINDS)과 따로 둔다 — 「사유 모름」은 출결 값의 종류가 아니다.
 */
const SYSTEM_KIND_LABEL = Object.fromEntries([...ATTENDANCE_KINDS, ['UNKNOWN', '사유 모름']]);

function CodesSettings() {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const [group, setGroup] = useState(CODE_GROUPS[0][0]);
  const r = useAsync((signal) => listCodesFull(group, { signal }, getAccessToken), `set-codes-${group}-${version}`);
  const row = useRowSave();
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState('');
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const meta = CODE_GROUPS.find(([g]) => g === group);
  const add = () => row.run('add', async () => {
    await createCode({ groupKey: group, label: label.trim(), systemKind: group === 'attendance_status' ? kind : undefined }, getAccessToken);
    setLabel(''); setKind('');
  });
  return (
    <Box title="사유 · 분류">
      <div className="mb-2 flex flex-wrap"><SegTabs tabs={CODE_GROUPS.map(([g, l]) => [g, l])} value={group} onChange={(g) => { setGroup(g); setEditing(null); row.clear(); }} ariaLabel="코드 묶음" /></div>
      {r.loading && !r.data && <Spinner label="값 불러오는 중" />}
      {r.error && <ErrorNotice error={r.error} onRetry={r.reload} compact />}
      {r.data && (
        <ul className="divide-y divide-gray-100">
          {r.data.map((c, i) => (
            <li key={c.id} className={`flex flex-wrap items-center gap-2 py-1.5 text-xs ${c.archived ? 'text-gray-400' : 'text-gray-900'}`}>
              <span className="w-6 text-gray-400">{i + 1}</span>
              {editing?.id === c.id ? (
                <>
                  <input aria-label="값 이름" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} className={`${compactInputBase} w-40`} />
                  {/* 빈 이름(공백만)은 저장하지 않는다 — 서버도 400 invalid 로 막는다(백엔드 0단계 전달 §4-E) */}
                  <AcademyButton type="button" disabled={!editing.label.trim()} title={editing.label.trim() ? undefined : '이름을 적어 주세요'} onClick={() => row.run(c.id, async () => { await updateCode(c.id, { label: editing.label.trim() }, getAccessToken); setEditing(null); })} variant="primary">저장</AcademyButton>
                  <AcademyButton type="button" onClick={() => setEditing(null)} variant="secondary">취소</AcademyButton>
                </>
              ) : (
                <>
                  <span className="font-medium">{c.label}</span>
                  <span className="font-mono text-gray-400">{c.code}</span>
                  {c.system_kind && <ToneBadge tone="info">{SYSTEM_KIND_LABEL[c.system_kind] || c.system_kind} 종류</ToneBadge>}
                  {c.archived && <ToneBadge tone="muted">감춤</ToneBadge>}
                  <span className="ml-auto flex flex-wrap justify-end gap-1">
                    <ActionButton icon={actionIcon('위로')} aria-label="위로" title="위로" disabled={i === 0} onClick={() => row.run(c.id, async () => {
                      const prev = r.data[i - 1];
                      await updateCode(c.id, { sortOrder: prev.sort_order }, getAccessToken);
                      await updateCode(prev.id, { sortOrder: c.sort_order }, getAccessToken);
                    })} />
                    <ActionButton icon={actionIcon('아래로')} aria-label="아래로" title="아래로" disabled={i === r.data.length - 1} onClick={() => row.run(c.id, async () => {
                      const next = r.data[i + 1];
                      await updateCode(c.id, { sortOrder: next.sort_order }, getAccessToken);
                      await updateCode(next.id, { sortOrder: c.sort_order }, getAccessToken);
                    })} />
                    <ActionButton iconOnly type="button" onClick={() => setEditing({ id: c.id, label: c.label })}>이름 수정</ActionButton>
                    <ActionButton iconOnly type="button" onClick={() => row.run(c.id, () => updateCode(c.id, { archived: !c.archived }, getAccessToken))}>{c.archived ? '되살리기' : '감추기'}</ActionButton>
                    <ActionButton iconOnly type="button" onClick={() => setConfirming(c)} danger>지우기</ActionButton>
                  </span>
                </>
              )}
              {row.errorOf(c.id) && <div className="w-full"><ErrorBox error={row.errorOf(c.id)} /></div>}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-gray-50 p-2">
        <input aria-label="새 값 이름" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`새 ${meta[1]}`} className={`${compactInputBase} w-44`} />
        {group === 'attendance_status' && (
          <select aria-label="시스템 종류" value={kind} onChange={(e) => setKind(e.target.value)} className={`${compactInputBase} w-28`}>
            <option value="">종류 선택</option>
            {ATTENDANCE_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        )}
        <AcademyButton type="button" disabled={!label.trim() || (group === 'attendance_status' && !kind)} onClick={add}>추가</AcademyButton>
      </div>
      <div className="mt-2"><ErrorBox error={row.errorOf('add')} /></div>
      {confirming && (
        <ConfirmDelete title={`${confirming.label} — ${meta[1]} 지우기`} onClose={() => setConfirming(null)} onConfirm={() => row.run(confirming.id, () => deleteCode(confirming.id, getAccessToken))}>
          {confirming.label}을(를) 삭제합니다.
        </ConfirmDelete>
      )}
    </Box>
  );
}

export const SETTINGS_TABS = [['classes', '반'], ['departments', '부서'], ['schools', '학교'], ['codes', '사유·분류']];

export function SettingsBody({ tab }) {
  if (tab === 'departments') return <DepartmentsSettings />;
  if (tab === 'schools') return <SchoolsSettings />;
  if (tab === 'codes') return <CodesSettings />;
  return null;
}
